import { Schema } from "effect";
import type { Chart } from "../../domain/models/chart";
import type { ChartSummaryDetails, SavedChartLibraryItem } from "../../services/chart-library";
import type { IncarnationCross } from "../../utils/calculations";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isPlanetPosition(value: unknown) {
  return (
    isRecord(value) &&
    typeof value.sign === "string" &&
    isFiniteNumber(value.degree) &&
    isFiniteNumber(value.minutes) &&
    isFiniteNumber(value.seconds) &&
    (value.decimalDegrees === undefined || isFiniteNumber(value.decimalDegrees))
  );
}

function isPlanetMap(value: unknown) {
  return isRecord(value) && Object.values(value).every(isPlanetPosition);
}

function isChart(value: unknown): value is Chart {
  return (
    isRecord(value) &&
    typeof value.name === "string" &&
    (value.Personality === undefined || isPlanetMap(value.Personality)) &&
    (value.Design === undefined || isPlanetMap(value.Design))
  );
}

function isIncarnationCross(value: unknown): value is IncarnationCross {
  if (
    !isRecord(value) ||
    typeof value.name !== "string" ||
    typeof value.gateNotation !== "string"
  ) {
    return false;
  }
  if (!isRecord(value.gates) || !isRecord(value.lines)) return false;
  return (
    [
      value.gates.personalitySunGate,
      value.gates.personalityEarthGate,
      value.gates.designSunGate,
      value.gates.designEarthGate,
      value.lines.personalitySunLine,
      value.lines.designSunLine,
    ].every(isFiniteNumber) &&
    (value.angle === "Right Angle" ||
      value.angle === "Left Angle" ||
      value.angle === "Juxtaposition") &&
    typeof value.officialName === "string" &&
    typeof value.birthPlaceTimeZone === "string"
  );
}

export const ChartPayloadSchema = Schema.declare((value): value is Chart => isChart(value));

export const IncarnationCrossSchema = Schema.declare((value): value is IncarnationCross =>
  isIncarnationCross(value),
);

function isChartSummary(value: unknown): value is ChartSummaryDetails {
  return (
    isRecord(value) &&
    typeof value.definition === "string" &&
    Array.isArray(value.gates) &&
    value.gates.every((item) => typeof item === "string") &&
    Array.isArray(value.channels) &&
    value.channels.every((item) => typeof item === "string") &&
    Array.isArray(value.variableTypes) &&
    value.variableTypes.every((item) => typeof item === "string")
  );
}

export const ChartSummarySchema = Schema.declare((value): value is ChartSummaryDetails =>
  isChartSummary(value),
);

export const SavedChartLibraryItemSchema = Schema.declare(
  (value): value is SavedChartLibraryItem =>
    isRecord(value) &&
    typeof value.id === "string" &&
    (value.username === undefined || typeof value.username === "string") &&
    typeof value.email === "string" &&
    typeof value.createdAt === "string" &&
    typeof value.birthDate === "string" &&
    typeof value.birthTime === "string" &&
    typeof value.location === "string" &&
    (value.phoneNumber === undefined || typeof value.phoneNumber === "string") &&
    typeof value.chartName === "string" &&
    typeof value.energyType === "string" &&
    typeof value.authority === "string" &&
    typeof value.profile === "string" &&
    typeof value.incarnationCrossName === "string" &&
    isChart(value.chart) &&
    isIncarnationCross(value.incarnationCross) &&
    typeof value.reportOutput === "string" &&
    (value.summary === undefined || value.summary === null || isChartSummary(value.summary)),
);

export interface FriendChartSummaryResponse {
  id: string;
  chartName: string;
  energyType: string;
  authority: string;
  profile: string;
  incarnationCrossName: string;
  chart: Chart | null;
  incarnationCross: IncarnationCross | null;
  summary?: ChartSummaryDetails | null;
}

export const FriendChartSummarySchema = Schema.declare(
  (value): value is FriendChartSummaryResponse =>
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.chartName === "string" &&
    typeof value.energyType === "string" &&
    typeof value.authority === "string" &&
    typeof value.profile === "string" &&
    typeof value.incarnationCrossName === "string" &&
    (value.chart === null || isChart(value.chart)) &&
    (value.incarnationCross === null || isIncarnationCross(value.incarnationCross)) &&
    (value.summary === undefined || value.summary === null || isChartSummary(value.summary)),
);

export const ClaimChartPreviewSchema = Schema.declare(
  (
    value,
  ): value is {
    chartName: string;
    phoneNumber?: string;
    birthDate: string;
    birthTime: string;
    location: string;
    energyType: string;
    authority: string;
    profile: string;
    incarnationCrossName: string;
    chart: Chart;
    incarnationCross: IncarnationCross;
    reportOutput: string;
  } =>
    isRecord(value) &&
    typeof value.chartName === "string" &&
    (value.phoneNumber === undefined || typeof value.phoneNumber === "string") &&
    typeof value.birthDate === "string" &&
    typeof value.birthTime === "string" &&
    typeof value.location === "string" &&
    typeof value.energyType === "string" &&
    typeof value.authority === "string" &&
    typeof value.profile === "string" &&
    typeof value.incarnationCrossName === "string" &&
    isChart(value.chart) &&
    isIncarnationCross(value.incarnationCross) &&
    typeof value.reportOutput === "string",
);

export type ClaimChartPreviewResponse = typeof ClaimChartPreviewSchema.Type;
